import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Skeleton,
  Autocomplete,
  TextField,
} from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { AffiliateLineChart } from "./AffiliateMetricsChart";
import AffiliatePerformanceTable from "./AffiliatePerformanceTable";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InsightsIcon from "@mui/icons-material/Insights";
import styles from "./style.module.css";

const COLOR_PALETTE = [
  "#38bdf8",
  "#1e3a5f",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#f97316",
];

const AffiliateDashboard = (props) => {
  const {
    totalAffiliates,
    activeAffiliates,
    affiliateMetrics,
    sixMonthMetrics,
    isSixMonthLoading,
    isLoading,
    isPageLevelError,
    locationDD,
    affiliateDD,
    selectedLocationIds,
    selectedAffiliateIds,
    onLocationChange,
    onAffiliateChange,
    onTotalAffiliatesClick,
    onActiveAffiliatesClick,
    performanceMetrics,
    performancePaginationInfo,
    isPerformanceLoading,
    onPerformancePageChange,
    affiliateReservations,
    isLoadingReservations,
    onFetchReservations,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate-dashboard", label: "Affiliate Dashboard" },
  ];

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const totalRevenueBodyTemplate = (rowData) => {
    return formatCurrency(rowData.totalRevenue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";
    return dateTimeString;
  };

  const customerNameTemplate = (rowData) => {
    return (
      `${rowData.renterFirstName || ""} ${rowData.renterLastName || ""}`.trim() ||
      "-"
    );
  };


  const selectedLocations = useMemo(() => {
    return locationDD.filter((loc) => selectedLocationIds?.includes(loc.id));
  }, [locationDD, selectedLocationIds]);

  const selectedAffiliates = useMemo(() => {
    return affiliateDD.filter((aff) => selectedAffiliateIds?.includes(aff.id));
  }, [affiliateDD, selectedAffiliateIds]);

  const renderKPICard = (title, value, icon, color, onClick) => {
    return (
      <Paper
        className={styles.kpiCard}
        elevation={0}
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        <Box className={styles.kpiIconBox} style={{ backgroundColor: color }}>
          {icon}
        </Box>
        <Box className={styles.kpiContent}>
          <Typography className={styles.kpiValue}>{value}</Typography>
          <Typography className={styles.kpiTitle}>{title}</Typography>
        </Box>
      </Paper>
    );
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" className={styles.dashboardContainer} sx={{ mt: 3, pb: 4 }}>
        <Breadcrumb list={breadcrumbD} />

        <Skeleton variant="text" width={120} height={28} sx={{ mb: 1, borderRadius: 1 }} />
        <Paper elevation={0} className={styles.filterCard} sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: "12px" }} />
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: "12px" }} />
          </Box>
        </Paper>

        <Skeleton variant="text" width={160} height={28} sx={{ mb: 1, borderRadius: 1 }} />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
          {[0, 1].map((i) => (
            <Paper key={i} elevation={0} className={styles.kpiCard} sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: "12px", flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="50%" height={32} />
                <Skeleton variant="text" width="70%" height={16} />
              </Box>
            </Paper>
          ))}
        </Box>

        <Skeleton variant="text" width={200} height={28} sx={{ mb: 1, borderRadius: 1 }} />
        <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
          {[0, 1].map((i) => (
            <Paper key={i} elevation={0} className={styles.chartCard} sx={{ p: 3, flex: "1 1 0", minWidth: 300 }}>
              <Skeleton variant="text" width={140} height={22} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />
            </Paper>
          ))}
        </Box>

        <Skeleton variant="text" width={220} height={28} sx={{ mt: 4, mb: 1, borderRadius: 1 }} />
        <Paper elevation={0} className={styles.filterCard} sx={{ p: 3 }}>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg"  className={styles.dashboardContainer}>
      <Breadcrumb list={breadcrumbD} />
      <Box className={styles.dashboardContainer}>
        {(locationDD.length > 0 || affiliateDD.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Box className={styles.sectionHeader}>
              <Typography className={styles.sectionTitle}>
                <LocationOnIcon sx={{ fontSize: 18 }} /> Filters
              </Typography>
            </Box>
            <Paper className={styles.filterCard} elevation={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={locationDD}
                getOptionLabel={(option) => option.name || ""}
                value={selectedLocations}
                onChange={(event, newValue) => {
                  onLocationChange(newValue.map((v) => v.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Location"
                    placeholder="Select locations..."
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={affiliateDD}
                getOptionLabel={(option) => option.name || ""}
                value={selectedAffiliates}
                onChange={(event, newValue) => {
                  onAffiliateChange(newValue.map((v) => v.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Affiliate"
                    placeholder="Select affiliates..."
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '12px', background: '#fff' },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
          </Box>
        )}

        <>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>
              <InsightsIcon sx={{ fontSize: 18 }} /> Overview
            </Typography>
          </Box>
          <Grid container spacing={2} className={styles.kpiGrid}>
            <Grid item xs={12} sm={6} md={6}>
              {renderKPICard(
                "Total Affiliates",
                totalAffiliates,
                <PeopleAltOutlinedIcon className={styles.kpiIcon} />,
                "#e3f2fd",
                onTotalAffiliatesClick,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              {renderKPICard(
                "Active Affiliates",
                activeAffiliates,
                <CheckCircleOutlineIcon className={styles.kpiIcon} />,
                "#e8f5e9",
                onActiveAffiliatesClick,
              )}
            </Grid>
          </Grid>
        </>

        <Grid container spacing={2} className={styles.chartsGrid}>
          <Grid item xs={12} md={6}>
            <AffiliateLineChart
              title="6-MONTH PERFORMANCE AFFILIATES"
              metrics={sixMonthMetrics}
              isLoading={isSixMonthLoading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AffiliateLineChart
              title="CURRENT MONTH — TOP AFFILIATES"
              metrics={affiliateMetrics}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        <AffiliatePerformanceTable
          affiliateMetrics={performanceMetrics}
          paginationInfo={performancePaginationInfo}
          isLoading={isPerformanceLoading}
          onPageChange={onPerformancePageChange}
          affiliateReservations={affiliateReservations}
          isLoadingReservations={isLoadingReservations}
          onFetchReservations={onFetchReservations}
        />
      </Box>
    </Container>
  );
};

export default AffiliateDashboard;
